import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
} from "@mui/material";
import { useEffect, useState } from "react";
import { requestEmailChange, updateCurrentUser } from "../../api/client";

export interface ChangeEmailDialogProps {
  open: boolean;
  onClose: () => void;
  currentEmail: string;
  accessToken: string;
  onSuccess: (message: string) => void;
}

export function ChangeEmailDialog({
  open,
  onClose,
  currentEmail,
  accessToken,
  onSuccess,
}: ChangeEmailDialogProps) {
  const [value, setValue] = useState(currentEmail);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setValue(currentEmail);
      setError(null);
    }
  }, [open, currentEmail]);

  const handleClose = () => {
    if (!submitting) onClose();
  };

  const handleSave = async () => {
    setError(null);
    setSubmitting(true);
    try {
      await requestEmailChange(accessToken, value.trim());
      onSuccess("Check your current and new email inboxes for confirmation links. Both must be clicked to complete the change.");
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to request email change");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { border: "1px solid", borderColor: "divider" } }}
    >
      <DialogTitle>Change email</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 0.5 }}>
          {error ? (
            <Alert severity="error" onClose={() => setError(null)}>
              {error}
            </Alert>
          ) : null}
          <TextField
            label="Email"
            type="email"
            value={value}
            onChange={(e) => {
              setValue(e.target.value);
              setError(null);
            }}
            fullWidth
            size="small"
            disabled={submitting}
            autoComplete="email"
          />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} disabled={submitting}>
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={submitting || value.trim().length === 0}
          onClick={handleSave}
        >
          {submitting ? "Saving…" : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export interface ChangePasswordDialogProps {
  open: boolean;
  onClose: () => void;
  currentEmail: string;
  accessToken: string;
  onSuccess: () => void;
}

export function ChangePasswordDialog({
  open,
  onClose,
  currentEmail,
  accessToken,
  onSuccess,
}: ChangePasswordDialogProps) {
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setNewPassword("");
      setConfirmPassword("");
      setError(null);
    }
  }, [open]);

  const handleClose = () => {
    if (!submitting) onClose();
  };

  const handleUpdate = async () => {
    setError(null);
    setSubmitting(true);
    try {
      await updateCurrentUser(accessToken, { password: newPassword.trim() });
      onSuccess();
      onClose();
    } catch (e) {
      setError(
        e instanceof Error ? e.message : "Failed to update password",
      );
    } finally {
      setSubmitting(false);
    }
  };

  const mismatch = confirmPassword.length > 0 && newPassword !== confirmPassword;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { border: "1px solid", borderColor: "divider" } }}
    >
      <DialogTitle>Change password</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 0.5 }}>
          {error ? (
            <Alert severity="error" onClose={() => setError(null)}>
              {error}
            </Alert>
          ) : null}
          <TextField
            label="Email"
            value={currentEmail}
            fullWidth
            size="small"
            disabled
            helperText="Account this password will apply to"
          />
          <TextField
            label="New password"
            type="password"
            value={newPassword}
            onChange={(e) => {
              setNewPassword(e.target.value);
              setError(null);
            }}
            fullWidth
            size="small"
            disabled={submitting}
            autoComplete="new-password"
            error={mismatch}
            helperText={
              mismatch ? "Passwords do not match" : "Minimum 8 characters"
            }
          />
          <TextField
            label="Confirm new password"
            type="password"
            value={confirmPassword}
            onChange={(e) => {
              setConfirmPassword(e.target.value);
              setError(null);
            }}
            fullWidth
            size="small"
            disabled={submitting}
            autoComplete="new-password"
            error={mismatch}
          />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} disabled={submitting}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="warning"
          disabled={
            submitting ||
            newPassword.trim().length < 8 ||
            newPassword !== confirmPassword
          }
          onClick={handleUpdate}
        >
          {submitting ? "Updating…" : "Update password"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
